import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SEO } from '@/components/SEO'
import { Activity, FileText, Users, Star } from 'lucide-react'

const stats = [
  {
    name: 'Total Projects',
    value: '12',
    icon: FileText,
    change: '+2',
    changeType: 'increase'
  },
  {
    name: 'Team Members',
    value: '3',
    icon: Users,
    change: '+1',
    changeType: 'increase'
  },
  {
    name: 'Activity Score',
    value: '89',
    icon: Activity,
    change: '+5',
    changeType: 'increase'
  },
  {
    name: 'Content Quality',
    value: '4.8',
    icon: Star,
    change: '+0.2',
    changeType: 'increase'
  }
]

const recentActivity = [
  {
    id: 1,
    type: 'project',
    title: 'SEO Strategy Document',
    action: 'created',
    timestamp: '2 hours ago'
  },
  {
    id: 2,
    type: 'content',
    title: 'Blog Post Analysis',
    action: 'updated',
    timestamp: '4 hours ago'
  },
  {
    id: 3,
    type: 'team',
    title: 'Team Meeting',
    action: 'scheduled',
    timestamp: '1 day ago'
  }
]

export default function Overview() {
  const { user } = useAuth()
  const firstName = user?.email?.split('@')[0] || 'User'

  return (
    <>
      <SEO
        title="Dashboard - WithWords"
        description="View your WithWords dashboard and manage your content strategy."
      />

      <div className="space-y-8">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold">Welcome back, {firstName}!</h1>
          <p className="text-muted-foreground mt-2">
            Here's what's happening with your projects today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon
            return (
              <Card key={stat.name}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.name}
                  </CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">
                    <span className={stat.changeType === 'increase' ? 'text-green-500' : 'text-red-500'}>
                      {stat.change}
                    </span>{' '}
                    from last month
                  </p>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center">
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {activity.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {activity.action} {activity.timestamp}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="cursor-pointer hover:bg-accent transition-colors">
            <CardHeader>
              <CardTitle className="text-lg">Create New Project</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Start a new content strategy project from scratch.
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-accent transition-colors">
            <CardHeader>
              <CardTitle className="text-lg">Analyze Content</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Run a semantic analysis on your existing content.
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-accent transition-colors">
            <CardHeader>
              <CardTitle className="text-lg">Team Collaboration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Invite team members and manage permissions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}